import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SatComboRow, SatCounts, SatOverview } from '../../api/assets';
import { SortDirToggle } from './parts';
import { SortDir } from '../../utils/sortDesc';
import { fmtN, pct } from './satisfactionText';

// 满意度统计页的图表件 (2026-10-23). 只用 Tailwind 现有色: 满意 = emerald, 不满意 / 低于整体 = rose,
// 样本不足 = slate 淡显; 每个图都带文字数值 (颜色不单独承载信息), 悬停 title 给完整数.

/** 样本量低于它的满意度只淡显、不标红 —— 与材料加工页「官方材料·官方参数」同一道线. */
export const MIN_RELIABLE_N = 30;
/** 低于整体满意度多少算「偏低」(需求方报告用 3 个点). */
const LOW_GAP = 0.03;

/** 满意度单元: 样本不足淡显 + 角标, 低于整体标红. */
export function RateText({ c, overall }: { c: SatCounts; overall: number | null }) {
    if (c.satisfaction_rate == null) return <span className="text-slate-300">—</span>;
    const thin = c.rated < MIN_RELIABLE_N;
    const low = !thin && overall != null && c.satisfaction_rate < overall - LOW_GAP;
    return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className={`font-semibold tabular-nums ${thin ? 'text-slate-400' : low ? 'text-rose-600' : 'text-emerald-600'}`}>
                {pct(c.satisfaction_rate)}
            </span>
            {thin && <span className="text-[10px] text-slate-400" title={`样本量 < ${MIN_RELIABLE_N}, 仅供参考`}>样本不足</span>}
            {low && <span className="text-[10px] px-1 rounded bg-rose-50 text-rose-600">偏低</span>}
        </span>
    );
}

/** 卡片外壳: 标题 + 一行结论/口径 + 右侧控件. */
export function Section({ no, title, desc, right, children }: {
    no?: string; title: string; desc?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode;
}) {
    return (
        <div className="card p-4">
            <div className="flex items-start gap-3 mb-3">
                {no && <div className="text-[11px] font-mono text-slate-400 pt-0.5">{no}</div>}
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-700">{title}</div>
                    {desc && <div className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{desc}</div>}
                </div>
                {right && <div className="ml-auto shrink-0 flex items-center gap-2">{right}</div>}
            </div>
            {children}
        </div>
    );
}

export interface RateBarRow extends SatCounts {
    key: string;
    label: string;
    sub?: string;
    onClick?: () => void;
}

/**
 * 满意度横条: 条长 = 满意度 (坐标从下限起, 拉开差距), 竖虚线 = 当前范围整体满意度.
 * 样本不足的行沉底淡显; 可切按满意度 / 样本量 / 加工次数排.
 */
export function RateBars({ rows, overall, maxRows = 15, labelWidth = 'w-40' }: {
    rows: RateBarRow[]; overall: number | null; maxRows?: number; labelWidth?: string;
}) {
    const [key, setKey] = useState<'rate' | 'rated' | 'count'>('rate');
    const [dir, setDir] = useState<SortDir>('asc');
    const [all, setAll] = useState(false);
    const rated = rows.filter((r) => r.satisfaction_rate != null);
    if (rated.length === 0) return <div className="text-xs text-slate-400 py-6 text-center">当前口径下没有评价数据</div>;
    const val = (r: RateBarRow) => (key === 'rate' ? r.satisfaction_rate ?? 0 : key === 'rated' ? r.rated : r.process_count);
    const sorted = [...rated].sort((a, b) => {
        const ta = a.rated < MIN_RELIABLE_N ? 1 : 0;
        const tb = b.rated < MIN_RELIABLE_N ? 1 : 0;
        if (key === 'rate' && ta !== tb) return ta - tb; // 样本少的 0% / 100% 不该顶在最前误导判断
        return dir === 'asc' ? val(a) - val(b) : val(b) - val(a);
    });
    const shown = all ? sorted : sorted.slice(0, maxRows);
    const rates = rated.map((r) => r.satisfaction_rate as number).concat(overall ?? 1);
    const lo = Math.max(0, Math.floor((Math.min(...rates) - 0.05) * 10) / 10);
    const x = (v: number) => `${((v - lo) / (1 - lo)) * 100}%`;
    return (
        <div>
            <div className="flex items-center gap-2 mb-2 text-[11px] text-slate-400">
                <span>坐标 {pct(lo, 0)} – 100%{overall != null && <> · 竖线 = 整体 {pct(overall)}</>}</span>
                <div className="ml-auto flex items-center gap-1">
                    {(['rate', 'rated', 'count'] as const).map((k) => (
                        <button key={k} type="button"
                            className={`px-1.5 py-0.5 rounded ${key === k ? 'bg-slate-700 text-white' : 'hover:bg-slate-100'}`}
                            onClick={() => setKey(k)}>
                            {k === 'rate' ? '满意度' : k === 'rated' ? '样本量' : '加工次数'}
                        </button>
                    ))}
                    <SortDirToggle value={dir} onChange={setDir} />
                </div>
            </div>
            <div className="space-y-1.5">
                {/* 列头: 与下方行同宽对齐 */}
                <div className="flex items-center gap-3 text-[11px] text-slate-400 pb-1 border-b border-slate-100">
                    <div className={`${labelWidth} shrink-0`}>名称</div>
                    <div className="flex-1">满意度 (条长)</div>
                    <div className="w-36 shrink-0 text-right">满意度</div>
                    <div className="w-28 shrink-0 text-right" title="n = 有评价的加工数 (满意 + 不满意); 差评 = 不满意次数">样本 · 差评数</div>
                </div>
                {shown.map((r) => {
                    const rate = r.satisfaction_rate as number;
                    const thin = r.rated < MIN_RELIABLE_N;
                    const low = !thin && overall != null && rate < overall - LOW_GAP;
                    const color = thin ? 'bg-slate-300' : low ? 'bg-rose-400' : 'bg-emerald-500';
                    return (
                        <div key={r.key}
                            className={`flex items-center gap-3 text-xs rounded px-1 -mx-1 ${r.onClick ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                            onClick={r.onClick}
                            title={`${r.label}${r.sub ? ` · ${r.sub}` : ''}\n满意度 ${pct(rate)} · 满意 ${r.satisfied} / 不满意 ${r.unsatisfied}\n样本 ${r.rated} · 加工 ${r.process_count.toLocaleString()} 次`}>
                            <div className={`${labelWidth} shrink-0 truncate`}>
                                <span className="text-slate-700">{r.label}</span>
                                {r.sub && <span className="text-slate-400 ml-1">{r.sub}</span>}
                            </div>
                            <div className="relative flex-1 h-3 rounded bg-slate-100">
                                <div className={`absolute left-0 top-0 h-3 rounded ${color}`} style={{ width: x(rate) }} />
                                {overall != null && (
                                    <div className="absolute -top-0.5 h-4 border-l border-dashed border-slate-500" style={{ left: x(overall) }} />
                                )}
                            </div>
                            <div className="w-36 shrink-0 text-right tabular-nums">
                                <RateText c={r} overall={overall} />
                            </div>
                            <div className="w-28 shrink-0 text-right text-[11px] text-slate-400 tabular-nums">
                                n={r.rated.toLocaleString()} · 差评 {r.unsatisfied}
                            </div>
                        </div>
                    );
                })}
            </div>
            {sorted.length > maxRows && (
                <button type="button" className="mt-2 text-[11px] text-brand-600 hover:underline" onClick={() => setAll(!all)}>
                    {all ? '收起' : `展开全部 ${sorted.length} 行`}
                </button>
            )}
            {rows.length > rated.length && (
                <div className="mt-2 text-[11px] text-slate-400">另有 {rows.length - rated.length} 行在当前口径下没有评价, 未列出.</div>
            )}
        </div>
    );
}

const COMPOSITION_META: Record<string, { label: string; desc: string; color: string }> = {
    official: { label: '官方材料', desc: '材料 id 能对上平台材料主数据 (legacy_mid)', color: 'bg-emerald-500' },
    legacy_missing: { label: '旧库 id · 平台无此材料', desc: '旧 xcs 库材料 id, 平台主数据里没有', color: 'bg-amber-400' },
    user_custom: { label: '用户素材', desc: '用户自建材料 (雪花 id 等)', color: 'bg-sky-400' },
    none: { label: '未选材料', desc: '上报未带材料 id (空 / 0)', color: 'bg-slate-300' },
};

/** 材料来源构成: 一条 100% 堆叠条 (段间 2px 间隙) + 下方图例表 (含各类满意度). */
export function CompositionBar({ rows }: { rows: SatOverview['composition'] }) {
    const total = rows.reduce((a, r) => a + r.process_count, 0) || 1;
    return (
        <div>
            <div className="flex h-4 gap-[2px] rounded overflow-hidden">
                {rows.map((r) => (
                    <div key={r.kind} className={COMPOSITION_META[r.kind]?.color || 'bg-slate-300'}
                        style={{ width: `${(r.process_count / total) * 100}%` }}
                        title={`${COMPOSITION_META[r.kind]?.label}: ${r.process_count.toLocaleString()} 次 (${pct(r.process_count / total)})`} />
                ))}
            </div>
            <div className="grid grid-cols-4 gap-3 mt-3">
                {rows.map((r) => {
                    const m = COMPOSITION_META[r.kind];
                    return (
                        <div key={r.kind} className="text-xs">
                            <div className="flex items-center gap-1.5">
                                <span className={`inline-block w-2.5 h-2.5 rounded-sm ${m?.color}`} />
                                <span className="font-medium text-slate-700">{m?.label || r.kind}</span>
                            </div>
                            <div className="mt-1 text-base font-semibold tabular-nums text-slate-800">{pct(r.process_count / total)}</div>
                            <div className="text-[11px] text-slate-400">
                                {fmtN(r.process_count)} 次 · 满意度 {pct(r.satisfaction_rate)} (n={r.rated})
                            </div>
                            <div className="text-[11px] text-slate-400">{m?.desc}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/** 月度: 每月两根柱 (灰 = 全部加工, 绿 = 官方材料), 同一纵轴; 下方一行官方材料满意度. */
export function MonthlyBars({ rows }: { rows: SatOverview['monthly'] }) {
    if (rows.length === 0) return <div className="text-xs text-slate-400 py-6 text-center">暂无数据</div>;
    const max = Math.max(1, ...rows.map((r) => r.total));
    return (
        <div>
            <div className="flex items-center gap-4 text-[11px] text-slate-500 mb-2">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-300" />全部加工</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />官方材料</span>
            </div>
            <div className="flex items-end gap-2 h-40 border-b border-slate-200">
                {rows.map((r) => (
                    <div key={r.month} className="flex-1 min-w-0 h-full flex items-end justify-center gap-[2px]"
                        title={`${r.month}\n全部加工 ${r.total.toLocaleString()} 次\n官方材料 ${r.process_count.toLocaleString()} 次 (${pct(r.total ? r.process_count / r.total : null)})\n官方材料满意度 ${pct(r.satisfaction_rate)} (n=${r.rated})`}>
                        <div className="w-1/3 max-w-[18px] rounded-t bg-slate-300" style={{ height: `${(r.total / max) * 100}%` }} />
                        <div className="w-1/3 max-w-[18px] rounded-t bg-emerald-500" style={{ height: `${(r.process_count / max) * 100}%` }} />
                    </div>
                ))}
            </div>
            <div className="flex gap-2 mt-1">
                {rows.map((r) => (
                    <div key={r.month} className="flex-1 min-w-0 text-center text-[10px] leading-tight">
                        <div className="text-slate-500">{r.month.slice(2)}</div>
                        <div className="text-slate-700 tabular-nums">{fmtN(r.process_count)}</div>
                        <div className="text-slate-400 tabular-nums">{pct(r.total ? r.process_count / r.total : null, 0)}</div>
                        <div className={`tabular-nums ${r.rated < MIN_RELIABLE_N ? 'text-slate-300' : 'text-emerald-600'}`}>{pct(r.satisfaction_rate, 0)}</div>
                    </div>
                ))}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">每列下方: 月份 / 官方材料加工次数 / 占全部加工 / 官方材料满意度 (样本不足淡显)</div>
        </div>
    );
}

/** 一级分类 name_en → 中文 (useCategoryText 产出, 由页面传入, 避免每格各起一个 hook). */
export type CatText = (nameEn?: string) => string;

export function MaterialCell({ xcode, name, type1, catText }: { xcode: string; name: string; type1?: string; catText: CatText }) {
    return (
        <div className="min-w-0">
            <Link to={`/materials/${encodeURIComponent(xcode)}`} className="text-brand-600 hover:underline">{name || xcode}</Link>
            <div className="text-[11px] text-slate-400 font-mono">{xcode}{type1 ? ` · ${catText(type1)}` : ''}</div>
        </div>
    );
}

/** 材料 × 设备 组合小表 (组合 Top / 重灾区 / 低分材料共用). */
export function ComboTable({ rows, overall, catText, withDevice = true, empty }: {
    rows: SatComboRow[]; overall: number | null; catText: CatText; withDevice?: boolean; empty?: string;
}) {
    if (rows.length === 0) return <div className="text-xs text-slate-400 py-6 text-center">{empty || '暂无数据'}</div>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-left text-[11px] text-slate-400 border-b border-slate-100 whitespace-nowrap">
                        <th className="py-1.5 pr-2 font-medium">#</th>
                        <th className="py-1.5 pr-2 font-medium">官方材料</th>
                        {withDevice && <th className="py-1.5 pr-2 font-medium">设备</th>}
                        {withDevice && <th className="py-1.5 pr-2 font-medium">瓦数 / 光源</th>}
                        <th className="py-1.5 pr-2 font-medium text-right">加工次数</th>
                        <th className="py-1.5 pr-2 font-medium text-right">满意 / 不满意</th>
                        <th className="py-1.5 font-medium text-right">满意度</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={`${r.xcode}|${r.device_module}`} className="border-b border-slate-50 whitespace-nowrap">
                            <td className="py-1.5 pr-2 text-slate-400 tabular-nums">{i + 1}</td>
                            <td className="py-1.5 pr-2"><MaterialCell xcode={r.xcode} name={r.material_name} type1={r.type1} catText={catText} /></td>
                            {withDevice && <td className="py-1.5 pr-2">{r.device_name || '—'}</td>}
                            {withDevice && <td className="py-1.5 pr-2 text-slate-500">{r.module || '未上报'}</td>}
                            <td className="py-1.5 pr-2 text-right tabular-nums">{r.process_count.toLocaleString()}</td>
                            <td className="py-1.5 pr-2 text-right tabular-nums">
                                <span className="text-emerald-600">{r.satisfied}</span>
                                <span className="text-slate-300"> / </span>
                                <span className="text-rose-500">{r.unsatisfied}</span>
                            </td>
                            <td className="py-1.5 text-right"><RateText c={r} overall={overall} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
